import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Grid,
  Paper,
  Chip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Description as DocumentIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import { DragDropFileUpload } from './DragDropFileUpload';

interface EquipmentFile {
  id: number;
  fileType: string;
  file: {
    id: number;
    name: string;
    url: string;
    mimeType?: string;
  };
}

interface EquipmentPrimaryFilesProps {
  equipmentId: number;
  files: EquipmentFile[];
}

const fileTypeConfig = {
  primaryPhoto: {
    label: 'Primary Photo',
    color: 'info' as const,
    folder: 'equipment/photos',
  },
};

export const EquipmentPrimaryFiles: React.FC<EquipmentPrimaryFilesProps> = ({
  equipmentId,
  files,
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [uploading, setUploading] = useState<string | null>(null);

  const getFileByType = (type: string) => {
    return files.find(f => f.fileType === type);
  };

  const handleFileUpload = async (fileType: string, uploadedFile: any) => {
    setUploading(fileType);
    try {
      await dataProvider.create(`equipment/${equipmentId}/primary-file`, {
        data: {
          fileId: uploadedFile.id,
          fileType,
        },
      });
      notify(`${fileTypeConfig[fileType].label} uploaded successfully`, { type: 'success' });
      refresh();
    } catch (error) {
      notify(`Failed to upload ${fileTypeConfig[fileType].label}`, { type: 'error' });
      console.error('Upload error:', error);
    } finally {
      setUploading(null);
    }
  };

  const handleFileDelete = async (fileType: string, fileId: number) => {
    if (window.confirm(`Are you sure you want to remove this ${fileTypeConfig[fileType].label}?`)) {
      try {
        await dataProvider.delete(`equipment/files/equipment/${equipmentId}/${fileId}`, {
          id: fileId,
        });
        notify(`${fileTypeConfig[fileType].label} removed successfully`, { type: 'success' });
        refresh();
      } catch (error) {
        notify(`Failed to remove ${fileTypeConfig[fileType].label}`, { type: 'error' });
        console.error('Delete error:', error);
      }
    }
  };

  const handleView = (file: EquipmentFile) => {
    window.open(file.file.url, '_blank');
  };

  return (
    <Grid container spacing={3}>
      {Object.entries(fileTypeConfig).map(([fileType, config]) => {
        const file = getFileByType(fileType);
        const isImage = fileType === 'primaryPhoto';
        
        return (
          <Grid item xs={12} md={4} key={fileType}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    {config.label}
                  </Typography>
                  <Chip
                    label={file ? 'Uploaded' : 'Not uploaded'}
                    color={file ? 'success' : 'default'}
                    size="small"
                  />
                </Box>

                {file ? (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    {isImage && file.file.mimeType?.startsWith('image/') ? (
                      <Box
                        component="img"
                        src={file.file.url}
                        alt={config.label}
                        sx={{
                          width: '100%',
                          height: 200,
                          objectFit: 'cover',
                          borderRadius: 1,
                          mb: 2,
                        }}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <DocumentIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" sx={{ flexGrow: 1 }}>
                          {file.file.name}
                        </Typography>
                      </Box>
                    )}
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        startIcon={<ViewIcon />}
                        onClick={() => handleView(file)}
                        fullWidth
                      >
                        View
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleFileDelete(fileType, file.file.id)}
                        fullWidth
                      >
                        Remove
                      </Button>
                    </Box>
                  </Paper>
                ) : (
                  <Box>
                    <DragDropFileUpload
                      onUploadSuccess={(uploadedFile) => handleFileUpload(fileType, uploadedFile)}
                      folderPath={config.folder}
                      acceptTypes={isImage ? 'image/*' : '.pdf,.doc,.docx'}
                      maxSizeMB={10}
                      title="Upload File"
                      subtitle={`Drag & drop or click to upload ${config.label.toLowerCase()}`}
                      buttonText="Select File"
                      disabled={uploading === fileType}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};